import { Products } from '../types/lucid-theme';
import { LinkInterface } from '../types/drupal-custom';
import getLinkPath from './getLinkPath';

interface GetDrupalMenuQuery {
  drupal: {
    menuLinkContentQuery: {
      entities?: LinkInterface[];
    };
  };
  chartDrupal?: {
    menuLinkContentQuery: {
      entities?: LinkInterface[];
    };
  };
  coDrupal?: {
    menuLinkContentQuery: {
      entities?: LinkInterface[];
    };
  };
}

type SiteQueryKeys = 'chartDrupal' | 'coDrupal';

export interface MenuLink extends LinkInterface {
  links?: MenuLink[];
}

const getI18nEntities = (entities: LinkInterface[], langPrefix: string) =>
  entities
    .filter(({ entityTranslations }) =>
      entityTranslations?.some(
        ({ entityLanguage, fieldDisableLanguage }) => langPrefix === entityLanguage!.id && !fieldDisableLanguage,
      ),
    )
    .map(({ entityTranslations, link, ...rest }) => {
      const entityTranslation = entityTranslations?.find(({ entityLanguage }) => langPrefix === entityLanguage!.id)!;

      const path = getLinkPath(link!.url, langPrefix);

      return { ...rest, ...entityTranslation, link: { url: { path } } };
    });

const getDrupalMenu = (query: GetDrupalMenuQuery, langPrefix: string, product?: Products.Site) => {
  const queryKey = product && (`${product}Drupal` as SiteQueryKeys);
  const queryData = (queryKey && query[queryKey]) || query.drupal;
  const entities = getI18nEntities(queryData.menuLinkContentQuery!.entities as LinkInterface[], langPrefix);

  const menu = new Map<string, MenuLink>();
  const parentNotFound: MenuLink[] = [];
  const lostChildren: MenuLink[] = [];

  const processLink = ({ entityUuid, parent, links = [], ...rest }: MenuLink, errorOnNoParent?: boolean) => {
    if (parent === null) {
      menu.set(`menu_link_content:${entityUuid}`, {
        links,
        ...rest,
      });
    } else {
      let parentObj = menu.get(parent!);

      // check children on second run through if needed
      if (!parentObj && errorOnNoParent) {
        menu.forEach(topLevel => {
          if (!parentObj && topLevel.links?.length) {
            parentObj = topLevel.links.find(link => {
              return `menu_link_content:${link.entityUuid}` === parent;
            });
          }
        });
      }

      // now check if we got anything from checking the children
      if (!parentObj && errorOnNoParent) {
        // No parent found on any run through.
        // Add to array and break the build so we can fix
        lostChildren.push({ entityUuid, parent, ...rest });
      } else if (!parentObj) {
        parentNotFound.push({ entityUuid, parent, ...rest });
      } else {
        parentObj.links?.push({ entityUuid, links: [], ...rest } as MenuLink);
      }
    }
  };

  entities.forEach(entity => processLink(entity));
  parentNotFound.forEach(entity => processLink(entity, true));

  // Create warning for children with missing parents to be addressed in CMS
  const warningCss = 'color: #4F4FE0; font-size: 20px; background: #F2BA00; border: 1px solid #4F4FE0'; // styles warning text in console
  if (lostChildren.length > 0) {
    lostChildren.forEach(link =>
      console.warn('%c Child with invalid parents - %s', warningCss, 'lost child link info:', {
        id: link.entityId,
        langCode: link.entityLanguage?.id,
        menu: link.entityBundle,
      }),
    );
  }

  return Array.from(menu.values());
};

export default getDrupalMenu;
